/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import EmptyCart from '../EmptyCart/EmptyCart';
import StoreCartLoadError from './StoreCartLoadError/StoreCartLoadError';
import StoreCartDesign from './StoreCartDesign/StoreCartDesign';
import StoreCartSummary from './StoreCartSummary/StoreCartSummary';
import { StoreCartUiVariationProvider } from './context/StoreCartUiVariationContext';
// Hidden from UI for now — keep for local UI variation switching
// import StoreCartUiVariationPicker from './StoreCartUiVariationPicker/StoreCartUiVariationPicker';
import StoreCartSetupButton from './StoreCartSetupButton/StoreCartSetupButton';
import { normalizeStoreCartApiResponse } from './helpers/normalizeStoreCartApiResponse';
import {
  isStoreCartEmptyApiError,
  isStoreCartLoadFailure,
} from './helpers/storeCartEmptyApiError.utils';
import {
  buildStoreCartDesignGroupMap,
  getSortedStoreCartDesignGroupEntries,
} from './helpers/storeCartDesignGroups.utils';
import {
  applyStoreCartOptionSelectionsToCartItems,
  buildStoreCartOptionSelectionsFromCartItems,
  filterCartItemsByStoreOptionSelections,
  serializeStoreCartDesignGroupSelection,
} from './helpers/storeCartOptionSelection.utils';
import { getStoreCartItemKeys } from './helpers/storeCartRemoval.utils';
import { useStoreCartSummarySheetAnimation } from './helpers/useStoreCartSummarySheetAnimation';
import {
  mergeStoreCartDesignGroupShellsFromCartItems,
  removeStoreCartDesignGroupSelection,
  removeStoreCartDesignGroupShell,
  clearDesignGroupApiStatuses,
  setStoreItems,
  syncStoreCartOptionSelectionsFromCartItems,
} from '@/store/reducers/storeCart';
import { currencyFormat } from '@/utilities/CommonUtils';
import './StoreCartPage.scss';
import { cloneCartFormItems, getCartForm } from '@/utilities/CartUtils';
import { useGetStoreItemsQuery } from '@/api/CartQuery';
import type { ICartForm, ICartItemDetail } from '@/common/interfaces/cart.interface';
import useViewport from '@/common/context/ViewPort/useViewPort';
import {
  DESKTOP_VIEW_PORT,
  MOBILE_VIEW_PORT,
  TAB_VIEW_PORT,
} from '@/common/context/ViewPort/ViewPort';
import { SHIPPING_METHODS, STORE_MODAL_EDGE_CLOSE_ICON_WRAPPER_STYLE } from '@/constants/Order';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { updateShipMethodProperties } from '@/store/reducers/shippingMethods';
import type { RootState } from '@/store';
import { getValidShipMethodsInfo, hasMoreThanThreeColors } from '@/utilities/ShippingUtils';
import type { IAdditionalShippingPriceRes } from '@/common/interfaces/quoter.interface';
import { useFeatureFlag } from '@/hooks/useAccount';
import { useCartActions } from '@/hooks/useCartActions';
import { useStoreCartDesignGroupMoqs } from '@/hooks/useStoreCartDesignGroupMoqs';
import { useSetupEventStore } from '@/hooks/useSetupEventStore';
import { Text } from '@/components/Text/Text';
import Icon from '@/components/Icon/Icon';
import Overlay from '@/components/Overlay/Overlay';
import { useStoreCartOpenUrlSync } from '@/hooks/useStoreCartOpenUrlSync';
import { STORE_CART_SUMMARY_LABEL } from '@/modules/Cart/StoreCart/storeCart.constants';

const STORE_CART_MOBILE_HEADER_OFFSET = '60px';
const STORE_CART_SUMMARY_MODAL_MAX_HEIGHT = `calc(100dvh - ${STORE_CART_MOBILE_HEADER_OFFSET})`;

const StoreCartPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useStoreCartOpenUrlSync(true);
  const { user } = useAppSelector((state: RootState) => state.user);
  const screen = useViewport(true);
  const isDesktopCartViewport = DESKTOP_VIEW_PORT.includes(screen);
  const hideSummaryModalEdgeClose =
    MOBILE_VIEW_PORT.includes(screen) || TAB_VIEW_PORT.includes(screen);
  const styleClass = 'store-cart-page--desktop';
  const [isCartEmpty, setIsCartEmpty] = useState(false);
  const [, setIsCartContainsRejectedItems] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const isSummarySheetEntered = useStoreCartSummarySheetAnimation(showSummaryModal);
  const { removeStoreItemInfo } = useCartActions();
  const userOrg = user?.accountType !== 'corporate' ? user?.organization ?? '' : '';
  const isDesignGroupFlow = useFeatureFlag(user, 'ENABLE_V4_ORDER_CREATION');
  const prevFormInitIdsRef = useRef('');
  const lastEmptiedApiKeysRef = useRef('');
  const designGroupShells = useAppSelector((state) => state.storeCart.designGroupShellsByKey);
  const optionSelectionsByGroupKey = useAppSelector(
    (state) => state.storeCart.optionSelectionsByGroupKey
  );

  const { isLoading, data, isFetching, error, isError } = useGetStoreItemsQuery({
    isStore: true,
  });

  const storeCartItems = useMemo(() => normalizeStoreCartApiResponse(data), [data]);
  const hasApiCartItems = storeCartItems.length > 0;

  const defaultSummaryValue = useMemo(
    () => ({
      subTotal: '',
      price: '',
      shipTypeKey: 'standard',
      shipTypeTitle: 'Standard',
      salesTax: 'TBD',
      total: '',
      estDeliveryDate: 'Feb 12',
      shipTypes: SHIPPING_METHODS,
      promoCode: '',
    }),
    []
  );

  const defaultCartValues = useMemo(
    () => ({
      cartDetails: [],
      ...defaultSummaryValue,
    }),
    [defaultSummaryValue]
  );

  const cartDetailsForm = useForm<ICartForm>({
    mode: 'all',
    defaultValues: defaultCartValues,
    reValidateMode: 'onChange',
  });

  const {
    watch: watchCartDetails,
    setValue: setCartDetails,
    getValues: getCartDetailsValue,
    reset,
  } = cartDetailsForm;

  const cartDetailsFormValue = watchCartDetails();
  const cartDetailsForRender = cartDetailsFormValue.cartDetails ?? [];
  const cartDetailsPricingKey = useMemo(
    () =>
      cartDetailsForRender
        .map(
          (item) =>
            `${item.cartItemId ?? item.id}:${item.quantity}:${item.price}:${item.shipType}:${
              item.isActive
            }`
        )
        .join('|'),
    [cartDetailsForRender]
  );
  const optionSelectionsKey = useMemo(
    () =>
      Object.entries(optionSelectionsByGroupKey)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(
          ([groupKey, selection]) =>
            `${groupKey}=${serializeStoreCartDesignGroupSelection(selection)}`
        )
        .join('|'),
    [optionSelectionsByGroupKey]
  );

  const apiCartFormItems = useMemo(
    () => (hasApiCartItems ? getCartForm(storeCartItems, userOrg) : []),
    [hasApiCartItems, storeCartItems, userOrg]
  );

  const resolvedCartItems = useMemo(() => {
    if (cartDetailsForRender.length > 0) {
      return cartDetailsForRender;
    }
    if (isCartEmpty) {
      return [];
    }
    return apiCartFormItems;
  }, [cartDetailsForRender, isCartEmpty, apiCartFormItems]);

  const resolvedCartShellMergeKey = useMemo(
    () => (resolvedCartItems.length ? getStoreCartItemKeys(resolvedCartItems) : ''),
    [resolvedCartItems]
  );

  useStoreCartDesignGroupMoqs(resolvedCartItems);

  useEffect(() => {
    if (!resolvedCartShellMergeKey) {
      return;
    }

    dispatch(mergeStoreCartDesignGroupShellsFromCartItems(resolvedCartItems));
  }, [dispatch, resolvedCartItems, resolvedCartShellMergeKey]);

  const hasRenderableDesignGroups = Object.keys(designGroupShells).length > 0;
  const { setupEventStore, isSettingUp } = useSetupEventStore();

  const filteredCartItemsForSetup = useMemo(
    () => filterCartItemsByStoreOptionSelections(resolvedCartItems, optionSelectionsByGroupKey),
    [resolvedCartItems, optionSelectionsByGroupKey]
  );

  const handleSetupStore = useCallback(() => {
    setupEventStore(filteredCartItemsForSetup, designGroupShells).catch(() => {
      /* setup errors are handled inside useSetupEventStore */
    });
  }, [designGroupShells, filteredCartItemsForSetup, setupEventStore]);

  const formProviderKey = useMemo(
    () =>
      getSortedStoreCartDesignGroupEntries(designGroupShells)
        .map(([groupKey]) => groupKey)
        .join('|'),
    [designGroupShells]
  );

  const summaryFormValue = watchCartDetails();

  const [itemShipPriceObj, setItemShipPriceObj] = useState<{
    [key: string]: IAdditionalShippingPriceRes[];
  }>();
  const [itemsMoqObj, setItemsMoqObj] = useState<{ [key: string]: number }>();

  const setShipTypes = useCallback(() => {
    const { shipTypeProperties } = getValidShipMethodsInfo(
      getCartDetailsValue('cartDetails'),
      getCartDetailsValue('shipTypeKey')
    );

    shipTypeProperties?.forEach((type) => type && dispatch(updateShipMethodProperties(type)));
  }, [dispatch, getCartDetailsValue]);

  const calculateSubTotal = (cartItems: ICartItemDetail[]) => {
    let subTotalValue = 0;
    let subtotalBeforeDiscount = 0;
    let flahShipPrice = 0;
    let expeditedShipPrice = 0;

    const hasInStockItem = cartItems.some((item: ICartItemDetail) => !item.outOfStock);

    const cartDetails = hasInStockItem
      ? cartItems.filter((item: ICartItemDetail) => !item.outOfStock)
      : cartItems;

    cartDetails.forEach((item: ICartItemDetail) => {
      subTotalValue += Number(item.quantity || 0) * Number(item.price || 0);
      subtotalBeforeDiscount += Number(item.quantity || 0) * Number(item.strikedPricePerUnit || 0);
      flahShipPrice += Number(item.quantity || 0) * Number(item.flashShipPrice || 0);
      expeditedShipPrice += Number(item.quantity || 0) * Number(item.expeditedShipPrice || 0);
    });

    const formattedSubTotal = currencyFormat(subTotalValue);
    if (getCartDetailsValue('subTotal') !== formattedSubTotal) {
      setCartDetails('subTotal', formattedSubTotal);
    }

    if (getCartDetailsValue('shipTypeKey') === 'flash') {
      const flashPrice = flahShipPrice;
      if (getCartDetailsValue('price') !== flashPrice) {
        setCartDetails('price', flashPrice);
      }
    }

    if (getCartDetailsValue('shipTypeKey') === 'expedited') {
      const expeditedPrice = expeditedShipPrice;
      if (getCartDetailsValue('price') !== expeditedPrice) {
        setCartDetails('price', expeditedPrice);
      }
    }

    const isMoreThan3Colors: boolean = hasMoreThanThreeColors(cartDetails);

    if (isMoreThan3Colors) {
      const nextShipTypeKey =
        getCartDetailsValue('shipTypeKey') === 'flash'
          ? 'standard'
          : getCartDetailsValue('shipTypeKey');
      const currentCartDetails = getCartDetailsValue('cartDetails') ?? [];
      const needsShipTypeUpdate = currentCartDetails.some(
        (item: ICartItemDetail) => item.shipType !== nextShipTypeKey
      );

      if (needsShipTypeUpdate) {
        setCartDetails(
          'cartDetails',
          cloneCartFormItems(currentCartDetails).map((item) => ({
            ...item,
            shipType: nextShipTypeKey,
          })),
          { shouldValidate: false }
        );
      }

      if (getCartDetailsValue('shipTypeKey') !== nextShipTypeKey) {
        setCartDetails('shipTypeKey', nextShipTypeKey, { shouldValidate: false });
      }
    }

    dispatch(
      updateShipMethodProperties({
        shipTypeKey: 'flash',
        properties: {
          price: Number(flahShipPrice.toFixed(2)),
        },
      })
    );
    dispatch(
      updateShipMethodProperties({
        shipTypeKey: 'expedited',
        properties: { price: Number(expeditedShipPrice.toFixed(2)) },
      })
    );

    return { subTotalValue, subtotalBeforeDiscount };
  };

  const updateTotal = useCallback(() => {
    const selectedCartItems = filterCartItemsByStoreOptionSelections(
      getCartDetailsValue('cartDetails'),
      optionSelectionsByGroupKey
    );
    const { subTotalValue, subtotalBeforeDiscount } = calculateSubTotal(selectedCartItems);
    let calculatedTotal = +subTotalValue;
    let calculatedStrikedTotal = subtotalBeforeDiscount;

    const shipPrice = getCartDetailsValue('price');

    if (typeof shipPrice === 'number') {
      calculatedTotal += shipPrice;
      calculatedStrikedTotal += shipPrice;
    }

    const formattedTotal = currencyFormat(Number(calculatedTotal));

    if (calculatedStrikedTotal > calculatedTotal) {
      const formattedStrikedTotal = currencyFormat(calculatedStrikedTotal);
      setCartDetails('strikedTotal', formattedStrikedTotal);
    }
    setCartDetails('total', formattedTotal);
  }, [summaryFormValue.price, cartDetailsFormValue.cartDetails, optionSelectionsByGroupKey]);

  const handleMoqObjOnRemoveItem = useCallback(
    (formIndex: number) => {
      const cartItems = getCartDetailsValue('cartDetails');
      const removedItem = cartItems[formIndex];

      if (itemsMoqObj) {
        const updatedItemsMoqObj = { ...itemsMoqObj };
        delete updatedItemsMoqObj[removedItem.id];
        setItemsMoqObj(updatedItemsMoqObj);
      }

      if (itemShipPriceObj) {
        const updatedItemShipPriceObj = { ...itemShipPriceObj };
        delete updatedItemShipPriceObj[removedItem.id];
        setItemShipPriceObj(updatedItemShipPriceObj);
      }
    },
    [getCartDetailsValue, itemsMoqObj, itemShipPriceObj]
  );

  const handleCartItemRemove = useCallback(
    async (formIndex: number) => {
      const cartItems = getCartDetailsValue('cartDetails');
      const itemToRemove = cartItems[formIndex];

      if (itemToRemove) {
        dispatch(mergeStoreCartDesignGroupShellsFromCartItems([itemToRemove]));

        const result = await removeStoreItemInfo(String(itemToRemove.cartItemId));

        if (result === 'success') {
          const updatedFields = cloneCartFormItems(
            cartItems.filter((_, index: number) => index !== formIndex)
          );
          reset({ ...defaultCartValues, cartDetails: updatedFields });
          prevFormInitIdsRef.current = updatedFields.length
            ? getStoreCartItemKeys(updatedFields)
            : '__empty__';

          if (!updatedFields.length) {
            lastEmptiedApiKeysRef.current = getStoreCartItemKeys(cartItems);
            setIsCartEmpty(true);
            prevFormInitIdsRef.current = '__empty__';
            dispatch(setStoreItems([]));
          }

          handleMoqObjOnRemoveItem(formIndex);
          updateTotal();
        }
      }
    },
    [
      dispatch,
      getCartDetailsValue,
      reset,
      defaultCartValues,
      handleMoqObjOnRemoveItem,
      updateTotal,
      removeStoreItemInfo,
    ]
  );

  const handleCartItemsRemove = useCallback(
    async (cartItemIds: string[]) => {
      const cartItems = getCartDetailsValue('cartDetails');
      const idsToRemove = new Set(cartItemIds.map(String));
      const itemsToRemove = cartItems.filter((item) => idsToRemove.has(String(item.cartItemId)));

      if (!itemsToRemove.length) {
        return;
      }

      dispatch(mergeStoreCartDesignGroupShellsFromCartItems(itemsToRemove));

      const removalResults = await Promise.all(
        itemsToRemove.map((item) => removeStoreItemInfo(String(item.cartItemId)))
      );

      if (!removalResults.every((result) => result === 'success')) {
        return;
      }

      const updatedFields = cloneCartFormItems(
        cartItems.filter((item) => !idsToRemove.has(String(item.cartItemId)))
      );
      reset({ ...defaultCartValues, cartDetails: updatedFields });
      prevFormInitIdsRef.current = updatedFields.length
        ? getStoreCartItemKeys(updatedFields)
        : '__empty__';

      if (!updatedFields.length) {
        lastEmptiedApiKeysRef.current = getStoreCartItemKeys(cartItems);
        setIsCartEmpty(true);
        prevFormInitIdsRef.current = '__empty__';
        dispatch(setStoreItems([]));
      }

      itemsToRemove.forEach((item) => {
        const removedIndex = cartItems.findIndex(
          (cartItem) => String(cartItem.cartItemId) === String(item.cartItemId)
        );
        if (removedIndex >= 0) {
          handleMoqObjOnRemoveItem(removedIndex);
        }
      });

      updateTotal();
    },
    [
      dispatch,
      getCartDetailsValue,
      reset,
      defaultCartValues,
      handleMoqObjOnRemoveItem,
      updateTotal,
      removeStoreItemInfo,
    ]
  );

  const handleDesignGroupShellRemove = useCallback(
    (groupKey: string) => {
      dispatch(removeStoreCartDesignGroupShell(groupKey));
      dispatch(removeStoreCartDesignGroupSelection(groupKey));
    },
    [dispatch]
  );

  const renderStoreCartDesigns = () => {
    const designGroups = buildStoreCartDesignGroupMap(resolvedCartItems);

    return getSortedStoreCartDesignGroupEntries(designGroupShells).map(([groupKey, shell]) => {
      const groupItems = designGroups[groupKey] ?? [];
      const primaryDesign = groupItems[0] ?? shell.cartDesign;

      return (
        <StoreCartDesign
          key={groupKey}
          cartDesign={primaryDesign}
          formIndex={primaryDesign.originalIndex ?? 0}
          onCartItemRemove={handleCartItemRemove}
          onCartItemsRemove={handleCartItemsRemove}
          onDesignGroupShellRemove={() => handleDesignGroupShellRemove(groupKey)}
          designGroupKey={groupKey}
          designGroupItems={
            groupItems.length ? groupItems.map((item) => item.originalIndex) : undefined
          }
          designGroupName={shell.designGroupName}
          forceDesktopStyle
        />
      );
    });
  };

  useEffect(() => {
    if (!apiCartFormItems.length || isLoading || isFetching) {
      return;
    }

    const currentKeys = getStoreCartItemKeys(apiCartFormItems);

    if (isCartEmpty) {
      if (prevFormInitIdsRef.current !== '__empty__' || !currentKeys.length) {
        return;
      }

      if (currentKeys === lastEmptiedApiKeysRef.current) {
        return;
      }
    } else if (currentKeys === prevFormInitIdsRef.current) {
      return;
    }

    prevFormInitIdsRef.current = currentKeys;
    lastEmptiedApiKeysRef.current = '';
    setIsCartEmpty(false);
    const optionSelections = buildStoreCartOptionSelectionsFromCartItems(apiCartFormItems);
    const syncedCartDetails = applyStoreCartOptionSelectionsToCartItems(
      apiCartFormItems,
      optionSelections
    );
    reset({ ...defaultCartValues, cartDetails: cloneCartFormItems(syncedCartDetails) });
    dispatch(clearDesignGroupApiStatuses());
    dispatch(syncStoreCartOptionSelectionsFromCartItems(optionSelections));

    const isSummaryContainsRejectedItems = apiCartFormItems.some(
      (item: ICartItemDetail) =>
        item.designRejectionInfo?.isDesignSelfBlockedForCheckout ||
        item.designRejectionInfo?.isDesignLicensingRejected
    );
    setIsCartContainsRejectedItems(isSummaryContainsRejectedItems);
    setCartDetails('shipTypeKey', apiCartFormItems[0].shipType);
    setShipTypes();
    setTimeout(() => {
      updateTotal();
    }, 100);
  }, [apiCartFormItems, isCartEmpty, isLoading, isFetching]);

  useEffect(() => {
    dispatch(setStoreItems(storeCartItems));
  }, [storeCartItems, dispatch]);

  useEffect(() => {
    if (getCartDetailsValue('cartDetails')?.length) {
      updateTotal();
    }
  }, [storeCartItems, cartDetailsPricingKey, optionSelectionsKey]);

  const handleAddItemsToCart = () => {
    navigate('/?openDesigns=store', { replace: true });
  };

  const renderPageShell = (content: ReactNode) => <div className={styleClass}>{content}</div>;

  const renderCartDesignsSection = () => (
    <div className="store-cart-page--desktop__wrapper">
      <div className="store-cart-page--desktop__wrapper__des-container">
        {renderStoreCartDesigns()}
      </div>
    </div>
  );

  const renderCompactBottomBar = () => (
    <div className="store-cart-page--desktop__bottom-fixed">
      {!showSummaryModal && (
        <button
          type="button"
          className="store-cart-page--desktop__bottom-fixed__summary-link"
          onClick={() => setShowSummaryModal(true)}
        >
          <Text as="span" size={14} color="#4698EA" weight="semibold">
            {STORE_CART_SUMMARY_LABEL}
          </Text>
          <Icon
            source="/src/assets/svg/chev_down.svg"
            className="store-cart-page--desktop__bottom-fixed__summary-link__icon"
            height="7px"
            width="12px"
          />
        </button>
      )}
      <div className="store-cart-page--desktop__bottom-bar">
        <StoreCartSetupButton
          className="store-cart-page--desktop__bottom-bar__setup-store-btn"
          iconClassName="store-cart-page--desktop__bottom-bar__setup-store-btn__icon"
          labelClassName="store-cart-page--desktop__bottom-bar__setup-store-btn__label"
          onClick={handleSetupStore}
          isLoading={isSettingUp}
        />
        <div className="store-cart-page--desktop__bottom-bar__description">
          <Text as="p" size={12} color="#8D90AA" weight="bold">
            Customize your branding, pricing, and shipping.
          </Text>
        </div>
      </div>
    </div>
  );

  const renderSummaryOverlay = () => (
    <Overlay
      isOpen={showSummaryModal}
      overlayClassName="cart-summary-overlay-backdrop"
      modalClassName={`store-cart-page--desktop__summary-modal ${
        isSummarySheetEntered ? 'open' : ''
      }`}
      onBackdropClick={() => setShowSummaryModal(false)}
      overlayStyles={{
        bottom: '0',
        height: STORE_CART_SUMMARY_MODAL_MAX_HEIGHT,
        pointerEvents: 'auto',
        zIndex: 1002,
      }}
      modalStyles={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 'auto',
        maxHeight: STORE_CART_SUMMARY_MODAL_MAX_HEIGHT,
        borderRadius: '20px 20px 0 0',
        background: '#FFFDFE',
        padding: '24px',
        overflowY: 'auto',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1003,
        display: 'flex',
        flexDirection: 'column',
      }}
      closeIconPath={hideSummaryModalEdgeClose ? undefined : '/src/assets/svg/close.svg'}
      closeIconWrapperClass={hideSummaryModalEdgeClose ? undefined : 'store-modal-edge-close'}
      closeIconWrapperStyles={
        hideSummaryModalEdgeClose ? undefined : STORE_MODAL_EDGE_CLOSE_ICON_WRAPPER_STYLE
      }
      onCloseClick={() => setShowSummaryModal(false)}
    >
      <StoreCartSummary showSetupStoreButton designGroupShells={designGroupShells} />
    </Overlay>
  );

  const isAwaitingFormHydration =
    hasApiCartItems && cartDetailsForRender.length === 0 && !isCartEmpty;

  if (((isLoading || isFetching) && !data && !isError) || isAwaitingFormHydration) {
    return renderPageShell(
      <div className="store-cart-inline-loader">
        <Spin tip="Loading store cart..." size="large" />
      </div>
    );
  }

  if (isStoreCartLoadFailure(isError, error)) {
    return renderPageShell(<StoreCartLoadError />);
  }

  if (
    isCartEmpty ||
    isStoreCartEmptyApiError(error) ||
    (!hasApiCartItems && !hasRenderableDesignGroups)
  ) {
    return renderPageShell(<EmptyCart handleAddItemsToCart={handleAddItemsToCart} isStoreCart />);
  }

  return (
    <ErrorBoundary>
      <StoreCartUiVariationProvider>
        {renderPageShell(
          <FormProvider {...cartDetailsForm} key={formProviderKey}>
            <div className={`${styleClass}__header ${isDesignGroupFlow ? 'cart-dg-header' : ''}`}>
              <Icon
                source="/src/assets/svg/chevron_down.svg"
                className={`${styleClass}__header__icon`}
                height="8px"
                width="10px"
                onClick={handleAddItemsToCart}
              />
              My Cart
              {/* Hidden from UI for now — keep for local UI variation switching
              <StoreCartUiVariationPicker
                className={`${styleClass}__header__ui-variation-picker`}
              />
              */}
              <div className={`${styleClass}__header__seperator`} />
            </div>
            <div className={`${styleClass}__layout`}>
              {renderCartDesignsSection()}
              {isDesktopCartViewport && (
                <div className={`${styleClass}__summary-sidebar cart-summary-container`}>
                  <StoreCartSummary showSetupStoreButton designGroupShells={designGroupShells} />
                </div>
              )}
            </div>
            {!isDesktopCartViewport && !showSummaryModal && renderCompactBottomBar()}
            {renderSummaryOverlay()}
          </FormProvider>
        )}
      </StoreCartUiVariationProvider>
    </ErrorBoundary>
  );
};

StoreCartPage.displayName = 'StoreCartPage';

export default StoreCartPage;
